import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { ChildSelector } from '@/components/builder/ChildSelector';
import { MediaOptionsGroup } from '@/components/builder/MediaOptionsGroup';
import { ThemeInput } from '@/components/builder/ThemeInput';
import { VoiceSelector, type Voice } from '@/components/builder/VoiceSelector';
import { EpisodeCountSelector, type EpisodeCount } from './EpisodeCountSelector';
import { SeriesPlanCard } from './SeriesPlanCard';
import { SeriesPlanLoading } from './SeriesPlanLoading';
import { useEntitlement } from '@/hooks/useEntitlement';
import { seriesCreditHint } from '@/features/series/credit-hint';
import { previewSeries, confirmSeries } from '@/features/series/api';
import type {
  SeriesPreviewResponse,
  PreviewSeriesRequest,
  ConfirmSeriesRequest,
} from '@/features/series/types';
import { ApiError, NetworkError } from '@/lib/api-client';
import { STRINGS } from '@/strings/strings';

const GOLD_CTA =
  'h-12 w-full bg-dream-gold px-8 text-base font-bold text-dream-navy hover:bg-dream-gold/90 disabled:opacity-60';

interface SeriesBuilderFormProps {
  worldSlug: 'aa' | 'kp';
  /**
   * Optional planning-state hoist. Fires whenever the internal `planning`
   * boolean changes so the parent route can hide surrounding controls
   * (e.g. the SeriesModeToggle) that are inoperable during the plan wait.
   * Pass a stable setter (e.g. `setSeriesPlanning` from useState) to avoid
   * effect re-runs on every parent render.
   */
  onPlanningChange?: (planning: boolean) => void;
}

/**
 * Serie-mode builder flow (single-plan redesign): form (theme + options +
 * voice) → "Staffel planen" (POST /series/preview, one Claude call → ONE
 * 5-episode plan; the wait shows the per-world generation loading screen) →
 * plan view → confirm (editable title) → "Staffel erstellen"
 * (POST /series/:id/confirm) → navigate to season view.
 *
 * Season options (PDF/Hörbuch/voice) are sent at PREVIEW: the confirm endpoint
 * does not persist them; the Series row carries them from preview and
 * generateEpisode reads them per episode.
 *
 * No 3-variant carousel, no reveal, no reroll button in this version.
 */
export function SeriesBuilderForm({ worldSlug, onPlanningChange }: SeriesBuilderFormProps) {
  const S = STRINGS.series.builder;
  const SS = STRINGS.builder.shared;
  const navigate = useNavigate();
  const { entitlement } = useEntitlement();

  // KP renders white-on-navy; AA renders navy-on-light. Labels theme
  // accordingly (cards stay on white bg).
  const dark = worldSlug === 'kp';
  const labelCls = dark ? 'text-sm text-dream-white' : 'text-sm text-dream-navy';

  const [childId, setChildId] = useState<string | null>(null);
  const [theme, setTheme] = useState('');
  const [episodeCount, setEpisodeCount] = useState<EpisodeCount>(5);
  // Opt-in, matching the single-story builder (both default unselected).
  const [pdf, setPdf] = useState(false);
  const [audio, setAudio] = useState(false);
  const [voice, setVoice] = useState<Voice>('male');

  const [preview, setPreview] = useState<SeriesPreviewResponse | null>(null);
  const [editedTitle, setEditedTitle] = useState('');

  const [planning, setPlanning] = useState(false);
  const [planError, setPlanError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  // A4: scroll the freshly-generated plan into view so it isn't left below
  // the fold under the form.
  const planRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (preview) planRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
  }, [preview]);

  // Notify the parent of every planning-state transition so surrounding
  // controls that are inoperable while planning (SeriesModeToggle) can hide.
  // Effect is inert unless the parent supplies the callback.
  useEffect(() => {
    onPlanningChange?.(planning);
  }, [planning, onPlanningChange]);

  const planDisabled = !theme.trim() || !childId || planning;

  function toMessage(err: unknown): string {
    if (err instanceof ApiError) return err.envelope?.user_message ?? S.error;
    if (err instanceof NetworkError) return STRINGS.auth.errors.networkError;
    return S.error;
  }

  async function handlePlan() {
    if (planDisabled) return;
    setPlanError(null);
    setPlanning(true);
    try {
      const body: PreviewSeriesRequest = {
        worldSlug,
        episodeCount,
        wantsAudio: audio,
        wantsPdf: pdf,
        narratorGender: voice,
      };
      if (childId) body.childId = childId;
      const trimmedTheme = theme.trim();
      if (trimmedTheme) body.theme = trimmedTheme;
      const res = await previewSeries(body);
      setPreview(res);
      // Prefill the editable season title with the LLM-generated seasonTitle
      // (falls back to the theme if the plan omitted one).
      setEditedTitle(res.plan.seasonTitle || theme.trim());
    } catch (err) {
      setPlanError(toMessage(err));
    } finally {
      setPlanning(false);
    }
  }

  async function handleCreate() {
    if (preview === null || creating) return;
    setCreateError(null);
    setCreating(true);
    try {
      const body: ConfirmSeriesRequest = {
        seasonArc: preview.plan.seasonArc,
        episodes: preview.plan.episodes.map((ep) => ({
          episodeNumber: ep.episodeNumber,
          title: ep.title,
          teaser: ep.teaser,
          outline: ep.outline,
          cliffhanger: ep.cliffhanger,
        })),
      };
      const trimmedTitle = editedTitle.trim();
      if (trimmedTitle) body.title = trimmedTitle;
      await confirmSeries(preview.seriesId, body);
      navigate(`/series/${preview.seriesId}`);
    } catch (err) {
      setCreateError(toMessage(err));
      setCreating(false);
    }
  }

  const credits = entitlement?.totalRemaining ?? 0;
  const creditHint = seriesCreditHint(episodeCount, credits);

  // The plan wait reuses the per-world generation loading screen.
  if (planning) {
    return <SeriesPlanLoading world={worldSlug} />;
  }

  return (
    <div className="flex flex-col gap-5">
      <div className="space-y-1.5">
        <Label htmlFor="series-theme" className={labelCls}>
          {S.themaLabel}
        </Label>
        <ThemeInput
          id="series-theme"
          value={theme}
          onChange={setTheme}
          placeholder={S.themaPlaceholder}
          maxLength={500}
          counterClassName={dark ? 'text-dream-white/70' : 'text-dream-navy'}
        />
      </div>

      <div className="space-y-2">
        <Label className={labelCls}>{S.episodeCountLabel}</Label>
        <EpisodeCountSelector value={episodeCount} onChange={setEpisodeCount} dark={dark} />
      </div>

      <ChildSelector
        id="series-child"
        label={SS.childSelectorLabel}
        {...(dark ? { labelClassName: 'text-dream-white' } : {})}
        value={childId}
        onChange={setChildId}
      />

      <div className="space-y-2">
        <Label className={labelCls}>{S.confirmOptionsLabel}</Label>
        <MediaOptionsGroup
          pdfActive={pdf}
          audioActive={audio}
          onPdfChange={setPdf}
          onAudioChange={setAudio}
        />
      </div>

      {audio &&
        (dark ? (
          <VoiceSelector
            value={voice}
            onChange={setVoice}
            containerClassName="bg-transparent"
            headingClassName="text-dream-white"
          />
        ) : (
          <VoiceSelector value={voice} onChange={setVoice} />
        ))}

      {planError && (
        <div
          role="alert"
          className="rounded-md border border-[rgba(226,75,74,0.4)] bg-[rgba(226,75,74,0.15)] p-3 text-sm text-[color:var(--color-error)]"
        >
          {planError}
        </div>
      )}

      <Button type="button" onClick={handlePlan} disabled={planDisabled} className={GOLD_CTA}>
        {S.planButton}
      </Button>

      {/* Single plan view + confirm block */}
      {preview && (
        <div ref={planRef} className="flex flex-col gap-3 scroll-mt-24">
          <SeriesPlanCard plan={preview.plan} />

          <div className="flex flex-col gap-4 rounded-lg border-[1.5px] border-dream-navy/15 bg-dream-white p-4">
            <div className="space-y-1.5">
              <Label htmlFor="series-title" className="text-sm text-dream-navy">
                {S.confirmTitleLabel}
              </Label>
              <ThemeInput
                id="series-title"
                value={editedTitle}
                onChange={setEditedTitle}
                placeholder={S.confirmTitleLabel}
                maxLength={120}
              />
            </div>

            <p className="text-xs text-dream-navy/70">{creditHint}</p>

            {createError && (
              <div
                role="alert"
                className="rounded-md border border-[rgba(226,75,74,0.4)] bg-[rgba(226,75,74,0.15)] p-3 text-sm text-[color:var(--color-error)]"
              >
                {createError}
              </div>
            )}

            <Button type="button" onClick={handleCreate} disabled={creating} className={GOLD_CTA}>
              {creating ? (
                <>
                  <Loader2 aria-hidden className="mr-2 h-4 w-4 animate-spin" />
                  {S.createButton}
                </>
              ) : (
                S.createButton
              )}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
